/**
 * Press-mode for the /scan kiosk — the platen on the screen, with the press's lasers on it.
 *
 * The lasers on the heat press are FIXED lines: they do not move easily, and one run presses dozens of
 * designs against the same lines. So the kiosk mirrors them exactly where they sit — configured once per
 * press in the setup sheet, by dragging each line on the platen or typing its inches — and every scanned
 * design is HUNG on them: its top edge on the "Back top" laser, its centre on "Centre", and so on. Nothing
 * to set per design. What the picture adds on every scan: which way is up, how big the transfer is, which
 * part of the art touches which laser (the files are trimmed to the ink), and where the far edge lands —
 * the fit check. The frame is a diagram, not a template: the screen is not true scale.
 *
 * The rail carries only what CHANGES per garment: the side pager, the design's size and edges, and any
 * warning. The lasers' positions and the platen never change during a shift, so they live one tap away in
 * the setup sheet rather than repeating on every scan (owner, 2026-09-10) — and the room they took goes to
 * the frame.
 *
 * What this file does NOT do: press anything. The press bar is the same `PressBar` the Classic tiles use,
 * driven by the page's unchanged handler; the art has no handler; every control here changes only what is
 * drawn. Geometry comes from `press-guide-lib.ts` (pure, tested); this file is layout.
 *
 * Scanner discipline (scan-capture.ts listens on the document): no native <select> anywhere on the kiosk
 * surface — a focused one would swallow every scan — so the setup is a Sheet of buttons; its inputs (laser
 * names and positions, custom platen dims) live inside that sheet, and the page pauses capture while it is
 * open.
 */
import { Check, Crosshair, Eye, EyeOff, ImageOff } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import type { components } from '@/lib/api/types.gen';
import { cn } from '@/lib/utils';
import { stripStyleCode } from './design-lib';
import {
  type AlignmentBox,
  ANY_LOCATION,
  boxToPercent,
  type CollarEdge,
  customPlaten,
  DEFAULT_LOAD_RULE,
  fallbackBox,
  findPlacementRule,
  fitsPlaten,
  fmtIn,
  H_ANCHORS,
  HOTRONIX_PLATENS,
  type InchBox,
  isWholeFileAlignment,
  LASER_LOCATIONS,
  LASER_SNAP_IN,
  type Laser,
  type LaserId,
  type LaserLine,
  laserAnchor,
  laserLines,
  lasersFromPrefs,
  type Overflow,
  type Platen,
  type PressPrefs,
  pagerOrder,
  placeOnLasers,
  platenFrameStyle,
  platenFromPrefs,
  pointerToInches,
  SEED_PLACEMENT_RULES,
  snapIn,
  toViewer,
  V_ANCHORS,
  WHOLE_FILE_ALIGNMENT,
} from './press-guide-lib';
import { PressBar } from './scan-press-bar';

type ScanResult = components['schemas']['ScanResolveResult'];
type ScanPlacement = NonNullable<ScanResult['placements']>[number];

/** What the page owns and Press-mode reads/asks to change: prefs, the side on screen, the sheet state. */
export interface PressModeControls {
  prefs: PressPrefs;
  onPrefsChange: (next: PressPrefs) => void;
  sideIndex: number;
  /** A deliberate pager tap — the page records it as the operator's intent (sticky side). */
  onSideChange: (index: number) => void;
  /** The setup sheet opened/closed — the page pauses scanner capture while it is open. */
  onPickerOpenChange: (open: boolean) => void;
  /** Auto-advance just moved the view; the press bar refuses a tap for a moment. */
  barDisarmed: boolean;
}

/** The header's per-device layout switch: Press-mode (the platen frame) or Classic (the two tiles). */
export function LayoutSwitch({
  layout,
  onChange,
}: {
  layout: PressPrefs['layout'];
  onChange: (layout: PressPrefs['layout']) => void;
}) {
  const opt = (value: PressPrefs['layout'], label: string) => (
    <button
      type="button"
      aria-pressed={layout === value}
      onClick={() => onChange(value)}
      className={cn(
        'whitespace-nowrap px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors',
        layout === value
          ? 'bg-foreground text-background'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {label}
    </button>
  );
  return (
    <fieldset className="inline-flex overflow-hidden rounded-full border">
      <legend className="sr-only">Layout</legend>
      {opt('press', 'Press-mode')}
      {opt('classic', 'Classic')}
    </fieldset>
  );
}

/** Everything the frame + rail derive for the side on screen, from the lib — computed once per render. */
function layoutFor(result: ScanResult, current: ScanPlacement, prefs: PressPrefs) {
  const platen = platenFromPrefs(prefs);
  const collarEdge: CollarEdge = prefs.collarEdge;
  const load = { ...DEFAULT_LOAD_RULE, collarEdge };
  const lasers = lasersFromPrefs(prefs, platen);
  const size =
    current.widthIn != null && current.heightIn != null
      ? { widthIn: current.widthIn, heightIn: current.heightIn }
      : undefined;
  const rule = findPlacementRule(
    SEED_PLACEMENT_RULES,
    current.location,
    result.sizeBand ?? 'adult',
    result.apparelKind,
  );
  let box: InchBox;
  let overflow: Overflow[] = [];
  const usedIds = new Set<string>();
  let note: string | null = null;
  let fallbackNote: string | null = null;
  let hasDesign = false;
  let align: AlignmentBox = WHOLE_FILE_ALIGNMENT;
  let crossIn: number | null = null;
  if (current.method === 'screen-print') {
    box = fallbackBox(platen);
    note = 'Screen-print side — pressed at the screen-print station.';
  } else if (!size) {
    box = fallbackBox(platen);
    note =
      'Size unknown — the design cannot be placed. (Rescan; if it persists the API predates the print-size fields.)';
  } else {
    // The design hangs on the beams by its ALIGNMENT box when the design library has one — the part
    // of the file that counts — and by the whole file when it does not, which is the identity case.
    align = current.alignment ?? WHOLE_FILE_ALIGNMENT;
    const placed = placeOnLasers(lasers, current.location, platen, size, load, rule, align);
    box = placed.box;
    hasDesign = true;
    // Where the vertical beam crosses the transfer, measured from its own left edge — the number that
    // bridges the picture and the physical film when the operator would rather not trust the drawing.
    if (placed.vLaser) crossIn = placed.vLaser.atIn - placed.box.x;
    overflow = fitsPlaten(box, platen);
    if (placed.hLaser) usedIds.add(placed.hLaser.id);
    if (placed.vLaser) usedIds.add(placed.vLaser.id);
    const missing = [
      placed.hLaser ? null : 'vertically',
      placed.vLaser ? null : 'horizontally',
    ].filter((m): m is string => m !== null);
    if (missing.length > 0) {
      fallbackNote = `No laser assigned to “${current.location}” ${missing.join(' or ')} — placed by the default rule. Assign one in Setup.`;
    }
  }
  const lines = laserLines(lasers, platen, collarEdge, usedIds);
  // The alignment box as a rectangle ON THE PLATEN, so the press can show — read-only — which part of
  // the transfer the beams were lined up with. Only drawn when it actually narrows the file: an
  // unaligned design must look exactly as it always has.
  const alignPlaten =
    hasDesign && !isWholeFileAlignment(align)
      ? {
          x: box.x + align.x * box.w,
          y: box.y + align.y * box.h,
          w: align.w * box.w,
          h: align.h * box.h,
        }
      : null;
  return {
    alignViewerBox: alignPlaten ? toViewer(alignPlaten, platen, collarEdge) : null,
    platen,
    collarEdge,
    box,
    viewerBox: toViewer(box, platen, collarEdge),
    overflow,
    lines,
    drawLines: prefs.guides ? lines : [],
    note,
    fallbackNote,
    hasDesign,
    align,
    crossIn,
  };
}

/**
 * The platen frame: the platen rectangle at true proportion, sized to the largest such box that fits
 * the working area (container-query units, the Classic tile's trick), the collar edge marked, the design
 * placed at its true size, the lasers across the whole platen — bright when they position the side on
 * screen, dim otherwise. Display-only on the kiosk surface: nothing here has a handler, so handling the
 * garment over the frame cannot mint a press. Inside the setup sheet the same frame is `editable`: each
 * line grows a drag handle (pointer or arrow keys) that reports a new position in inches.
 */
function PlatenFrame({
  platen,
  collarEdge,
  viewerBox,
  alignViewerBox = null,
  overflow,
  lines,
  previewUrl,
  alt,
  showDesign = true,
  editable = false,
  onLaserMove,
}: {
  platen: Platen;
  collarEdge: CollarEdge;
  viewerBox: InchBox;
  /** The part of the transfer the beams were lined up with — drawn read-only, or null when unset. */
  alignViewerBox?: InchBox | null;
  overflow: Overflow[];
  lines: LaserLine[];
  previewUrl?: string;
  alt: string;
  showDesign?: boolean;
  editable?: boolean;
  /** Editable frame only: a laser was dragged/nudged to a new COLLAR-frame position, inches. */
  onLaserMove?: (id: LaserId, atIn: number) => void;
}) {
  const [broken, setBroken] = useState(false);
  const frameRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<LaserLine | null>(null);
  const far = collarEdge === 'far';
  const pct = (n: number, d: number) => `${((100 * n) / d).toFixed(3)}%`;

  const moveTo = (l: LaserLine, clientX: number, clientY: number) => {
    const r = frameRef.current?.getBoundingClientRect();
    if (!r || !onLaserMove) return;
    const atIn =
      l.orientation === 'h'
        ? pointerToInches(clientY, r.top, r.height, platen.deepIn, !far)
        : pointerToInches(clientX, r.left, r.width, platen.acrossIn, !far);
    onLaserMove(l.id, atIn);
  };
  const nudge = (l: LaserLine, dir: 1 | -1) => {
    if (!onLaserMove) return;
    const extent = l.orientation === 'h' ? platen.deepIn : platen.acrossIn;
    // Arrow keys move in the direction shown on SCREEN; a near collar inverts the collar-frame delta.
    const delta = (far ? dir : -dir) * LASER_SNAP_IN;
    onLaserMove(l.id, Math.min(extent, Math.max(0, snapIn(l.at + delta))));
  };

  return (
    <div className="flex min-h-0 items-center justify-center" style={{ containerType: 'size' }}>
      <div
        ref={frameRef}
        data-testid="platen-frame"
        className="relative overflow-hidden rounded-xl border border-zinc-600 bg-zinc-900"
        style={platenFrameStyle(platen)}
      >
        {/* The collar edge — the platen edge the shirt's collar sits on, the reference for every laser
            position. Top of the screen for a far collar, bottom for a near one. */}
        <div
          aria-hidden
          className={cn('absolute inset-x-0 h-[3px] bg-zinc-100', far ? 'top-0' : 'bottom-0')}
        />
        <span
          className={cn(
            'absolute left-2 text-[10px] font-bold tracking-[0.2em] text-zinc-300',
            far ? 'top-1.5' : 'bottom-1.5',
          )}
        >
          COLLAR
        </span>

        {/* The design at its true print size, hung on the lasers. Black well so light/halftone art on a
            transparent webp stays visible (the Classic tile's rationale). A near collar shows the shirt
            upside down to the operator, so the art turns with it. */}
        {showDesign && (
          <div
            data-testid="design-box"
            className={cn(
              'absolute bg-black outline',
              overflow.length > 0 ? 'outline-2 outline-red-500' : 'outline-1 outline-zinc-600',
            )}
            style={boxToPercent(viewerBox, platen)}
          >
            {previewUrl && !broken ? (
              <img
                src={previewUrl}
                alt={alt}
                loading="lazy"
                className="h-full w-full object-contain"
                style={far ? undefined : { transform: 'rotate(180deg)' }}
                onError={() => setBroken(true)}
              />
            ) : (
              <div className="grid h-full w-full place-items-center">
                <ImageOff className="size-8 text-muted-foreground" />
              </div>
            )}
          </div>
        )}

        {/* Read-only: which part of the transfer the guides were lined up with. Nothing here is
            draggable — the press is for executing, and the art has no handler. It is here so that a
            design aligned WRONG is visible to the person who would otherwise silently compensate. */}
        {showDesign && alignViewerBox && (
          <div
            data-testid="align-overlay"
            aria-hidden
            className="pointer-events-none absolute border border-emerald-300 border-dashed shadow-[0_0_0_1px_rgba(0,0,0,0.55)]"
            style={boxToPercent(alignViewerBox, platen)}
          />
        )}

        {/* The lasers, spanning the whole platen like the physical line does. Laser red; bright when the
            line positions this side, dim when it belongs to another side. */}
        {lines.map((l) => {
          const pos =
            l.orientation === 'h'
              ? { top: pct(l.atViewer, platen.deepIn) }
              : { left: pct(l.atViewer, platen.acrossIn) };
          const line = (
            <div
              data-testid="guide-line"
              data-laser={l.id}
              data-used={l.used ? 'true' : 'false'}
              aria-hidden
              className={cn(
                'absolute bg-[#ff3b30]',
                l.orientation === 'h' ? 'inset-x-0 h-0.5' : 'inset-y-0 w-0.5',
                l.used
                  ? 'shadow-[0_0_8px_2px_rgba(255,59,48,0.45)]'
                  : 'opacity-40 shadow-[0_0_4px_1px_rgba(255,59,48,0.25)]',
              )}
              style={editable ? undefined : pos}
            />
          );
          if (!editable) return <div key={l.id}>{line}</div>;
          // Editable: a 24px handle centred on the line, draggable with a pointer, nudged with arrows.
          return (
            <div
              key={l.id}
              className={cn(
                'absolute',
                l.orientation === 'h'
                  ? 'inset-x-0 h-6 -translate-y-1/2'
                  : 'inset-y-0 w-6 -translate-x-1/2',
              )}
              style={pos}
            >
              <div className="relative h-full w-full">
                <div
                  className={cn(
                    'absolute',
                    l.orientation === 'h' ? 'inset-x-0 top-1/2' : 'inset-y-0 left-1/2',
                  )}
                >
                  {line}
                </div>
                <button
                  type="button"
                  aria-label={`Drag ${l.label}`}
                  title={`${l.label} — drag, or use the arrow keys`}
                  className={cn(
                    'absolute inset-0 touch-none rounded bg-transparent focus-visible:outline focus-visible:outline-2 focus-visible:outline-white',
                    l.orientation === 'h' ? 'cursor-row-resize' : 'cursor-col-resize',
                  )}
                  onPointerDown={(e) => {
                    dragRef.current = l;
                    e.currentTarget.setPointerCapture(e.pointerId);
                    moveTo(l, e.clientX, e.clientY);
                  }}
                  onPointerMove={(e) => {
                    if (dragRef.current?.id === l.id) moveTo(l, e.clientX, e.clientY);
                  }}
                  onPointerUp={() => {
                    dragRef.current = null;
                  }}
                  onPointerCancel={() => {
                    dragRef.current = null;
                  }}
                  onKeyDown={(e) => {
                    const forward =
                      l.orientation === 'h' ? e.key === 'ArrowDown' : e.key === 'ArrowRight';
                    const back =
                      l.orientation === 'h' ? e.key === 'ArrowUp' : e.key === 'ArrowLeft';
                    if (forward || back) {
                      e.preventDefault();
                      nudge(l, forward ? 1 : -1);
                    }
                  }}
                />
                <span
                  className={cn(
                    'pointer-events-none absolute rounded bg-black/70 px-1 py-0.5 text-[10px] font-bold text-[#ff8a80]',
                    l.orientation === 'h'
                      ? 'top-1/2 right-1 -translate-y-1/2'
                      : 'top-1 left-1/2 -translate-x-1/2',
                  )}
                >
                  {l.label}
                </span>
              </div>
            </div>
          );
        })}
        {/* The platen's size, quietly, in the corner opposite the collar label — the frame IS the platen,
            so this is the only place the dimensions need to appear on the kiosk surface. */}
        <span
          className={cn(
            'pointer-events-none absolute right-2 font-mono text-[10px] text-zinc-500',
            far ? 'bottom-1.5' : 'top-1.5',
          )}
        >
          {fmtIn(platen.acrossIn)} × {fmtIn(platen.deepIn)} in
        </span>
      </div>
    </div>
  );
}

/** A small segmented control of buttons, used throughout the sheet. Neutral high-contrast for the
 *  selected state — red is the beam, and a wall of red chips would read as errors. */
function Segmented<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: T | null;
  options: readonly { value: T; label: string }[];
  onChange: (v: T) => void;
  ariaLabel: string;
}) {
  return (
    <fieldset className="flex overflow-hidden rounded-lg border">
      <legend className="sr-only">{ariaLabel}</legend>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'min-h-[40px] flex-1 px-2 text-xs font-bold',
            value === o.value ? 'bg-foreground text-background' : 'text-muted-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </fieldset>
  );
}

const ANCHOR_LABELS: Record<string, string> = {
  top: 'Top edge',
  centerH: 'Centre',
  bottom: 'Bottom edge',
  left: 'Left edge',
  centerV: 'Centre',
  right: 'Right edge',
};

const SECTION_LABEL = 'text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground';

/**
 * The setup sheet — everything about THIS press, set once. The platen (catalog or custom, and which edge
 * the collar sits on), then the lasers: a live platen frame with every laser as a draggable line, and a
 * card per laser (on/off, name, orientation, position in inches, which design edge sits on it, which print
 * locations it positions). Every change saves to this device's prefs immediately.
 */
function PressSetupSheet({
  open,
  onOpenChange,
  prefs,
  onPrefsChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prefs: PressPrefs;
  onPrefsChange: (next: PressPrefs) => void;
}) {
  const [across, setAcross] = useState('');
  const [deep, setDeep] = useState('');
  const platen = platenFromPrefs(prefs);
  const collarEdge = prefs.collarEdge;
  const custom = prefs.customPlaten
    ? customPlaten(prefs.customPlaten.acrossIn, prefs.customPlaten.deepIn)
    : null;
  const platens: Platen[] = custom ? [...HOTRONIX_PLATENS, custom] : [...HOTRONIX_PLATENS];
  const attempt = customPlaten(Number(across), Number(deep));
  const useCustom = () => {
    if (!attempt) return;
    onPrefsChange({
      ...prefs,
      platenId: attempt.id,
      customPlaten: { acrossIn: attempt.acrossIn, deepIn: attempt.deepIn },
    });
    setAcross('');
    setDeep('');
  };

  const lasers = lasersFromPrefs(prefs, platen);
  const update = (id: LaserId, patch: Partial<Laser>) =>
    onPrefsChange({ ...prefs, lasers: lasers.map((l) => (l.id === id ? { ...l, ...patch } : l)) });
  const allIds = new Set(lasers.filter((l) => l.enabled).map((l) => l.id));
  const lines = laserLines(lasers, platen, collarEdge, allIds);
  const toggleLocation = (l: Laser, loc: string) => {
    const has = l.usedFor.includes(loc);
    // "Any" is exclusive with the specific tags — one or the other names the laser's job.
    let next: string[];
    if (loc === ANY_LOCATION) next = has ? [] : [ANY_LOCATION];
    else if (has) next = l.usedFor.filter((x) => x !== loc);
    else next = [...l.usedFor.filter((x) => x !== ANY_LOCATION), loc];
    update(l.id, { usedFor: next });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* `dark`: the sheet portals to <body>, outside the kiosk's forced-dark tree. Its own scroll region
          below; the close button is a sibling of it, never inside it. */}
      <SheetContent side="right" className="dark w-full bg-background text-foreground sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>Press setup</SheetTitle>
          <SheetDescription>
            The platen on this press and where its lasers fall. Set once — every design hangs on
            these lines. Remembered on this device.
          </SheetDescription>
        </SheetHeader>
        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 pb-4">
          <section className="flex flex-col gap-2">
            <h3 className={SECTION_LABEL}>Platen · across × deep</h3>
            <div className="grid grid-cols-3 gap-2">
              {platens.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  aria-pressed={p.id === prefs.platenId}
                  aria-label={p.name}
                  title={p.name}
                  onClick={() => onPrefsChange({ ...prefs, platenId: p.id })}
                  className={cn(
                    'min-h-[44px] rounded-lg border px-2 font-mono text-sm',
                    p.id === prefs.platenId
                      ? 'bg-foreground font-bold text-background'
                      : 'text-muted-foreground hover:bg-muted/40',
                  )}
                >
                  {fmtIn(p.acrossIn)} × {fmtIn(p.deepIn)}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex items-end gap-2">
                <Input
                  type="number"
                  inputMode="decimal"
                  step={0.5}
                  min={1}
                  max={40}
                  value={across}
                  onChange={(e) => setAcross(e.target.value)}
                  aria-label="Across, inches"
                  placeholder="across"
                  className="h-10 w-24"
                />
                <span className="pb-2 text-muted-foreground">×</span>
                <Input
                  type="number"
                  inputMode="decimal"
                  step={0.5}
                  min={1}
                  max={40}
                  value={deep}
                  onChange={(e) => setDeep(e.target.value)}
                  aria-label="Deep, inches"
                  placeholder="deep"
                  className="h-10 w-24"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-[40px]"
                  disabled={!attempt}
                  onClick={useCustom}
                >
                  Use custom
                </Button>
              </div>
              <div className="min-w-[14rem] flex-1">
                <div className={cn(SECTION_LABEL, 'mb-1')}>Collar sits on the</div>
                <Segmented
                  ariaLabel="Collar edge"
                  value={prefs.collarEdge}
                  options={[
                    { value: 'far', label: 'Far edge' },
                    { value: 'near', label: 'Near edge' },
                  ]}
                  onChange={(collarEdge) => onPrefsChange({ ...prefs, collarEdge })}
                />
              </div>
            </div>
          </section>

          <section className="flex flex-col gap-3">
            <h3 className={SECTION_LABEL}>
              Lasers · drag each line onto its beam, or type the inches
            </h3>
            {/* `grid`: the frame's wrapper is a size container (100cqh), which only has a height when its
                parent STRETCHES it — a grid cell does, a plain block gives it zero and the frame vanishes.
                `shrink-0`: this box is a flex item in a scrolling column, and a flex item SHRINKS along the
                main axis before the column overflows — without it the 18rem collapses to its padding. */}
            <div className="grid h-72 shrink-0 rounded-xl border bg-zinc-950/40 p-2">
              <PlatenFrame
                platen={platen}
                collarEdge={collarEdge}
                viewerBox={{ x: 0, y: 0, w: 0, h: 0 }}
                overflow={[]}
                lines={lines}
                alt=""
                showDesign={false}
                editable
                onLaserMove={(id, atIn) => update(id, { atIn })}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {lasers.map((l) => {
                const extent = l.orientation === 'h' ? platen.deepIn : platen.acrossIn;
                const anchors = l.orientation === 'h' ? H_ANCHORS : V_ANCHORS;
                const offPlaten = l.atIn > extent;
                return (
                  <div
                    key={l.id}
                    className={cn(
                      'flex flex-col gap-2 rounded-lg border p-3',
                      !l.enabled && 'opacity-60',
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        aria-pressed={l.enabled}
                        aria-label={`${l.label || l.id} ${l.enabled ? 'on' : 'off'}`}
                        onClick={() => update(l.id, { enabled: !l.enabled })}
                        className={cn(
                          'min-h-[40px] w-14 flex-none rounded-lg border text-xs font-bold',
                          l.enabled ? 'bg-emerald-600 text-white' : 'text-muted-foreground',
                        )}
                      >
                        {l.enabled ? 'On' : 'Off'}
                      </button>
                      <Input
                        value={l.label}
                        maxLength={24}
                        aria-label={`${l.label || l.id} name`}
                        placeholder="Name"
                        onChange={(e) => update(l.id, { label: e.target.value })}
                        className="h-10 min-w-0 flex-1"
                      />
                    </div>
                    <Segmented
                      ariaLabel={`${l.label} orientation`}
                      value={l.orientation}
                      options={[
                        { value: 'h', label: 'Horizontal' },
                        { value: 'v', label: 'Vertical' },
                      ]}
                      onChange={(orientation) =>
                        update(l.id, {
                          orientation,
                          anchor: laserAnchor({ orientation, anchor: l.anchor }),
                        })
                      }
                    />
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        inputMode="decimal"
                        step={LASER_SNAP_IN}
                        min={0}
                        max={extent}
                        value={l.atIn}
                        aria-label={`${l.label} position, inches`}
                        onChange={(e) => {
                          const n = e.target.valueAsNumber;
                          if (Number.isFinite(n))
                            update(l.id, { atIn: Math.min(40, Math.max(0, n)) });
                        }}
                        className="h-10 w-24"
                      />
                      <span className="text-xs text-muted-foreground">
                        {l.orientation === 'h' ? 'in from collar edge' : 'in from left edge'}
                        {offPlaten && (
                          <span className="text-amber-400">
                            {' '}
                            · off the platen ({fmtIn(extent)} in)
                          </span>
                        )}
                      </span>
                    </div>
                    <div>
                      <div className={cn(SECTION_LABEL, 'mb-1')}>Design sits on it by its</div>
                      <Segmented
                        ariaLabel={`${l.label} design anchor`}
                        value={laserAnchor(l)}
                        options={anchors.map((a) => ({ value: a, label: ANCHOR_LABELS[a] ?? a }))}
                        onChange={(anchor) => update(l.id, { anchor })}
                      />
                    </div>
                    <div>
                      <div className={cn(SECTION_LABEL, 'mb-1')}>Used for</div>
                      <div className="flex flex-wrap gap-1.5">
                        {[ANY_LOCATION, ...LASER_LOCATIONS].map((loc) => {
                          const on = l.usedFor.includes(loc);
                          return (
                            <button
                              key={loc}
                              type="button"
                              aria-pressed={on}
                              onClick={() => toggleLocation(l, loc)}
                              className={cn(
                                'min-h-[32px] rounded-md border px-2 text-xs font-semibold',
                                on ? 'bg-foreground text-background' : 'text-muted-foreground',
                              )}
                            >
                              {loc === ANY_LOCATION ? 'Any' : loc}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Landscape or portrait, live. `matchMedia` is absent in jsdom (and could be blocked), so the hook
 * defaults to PORTRAIT — the single-tile layout every existing test renders — and only a real browser
 * reports landscape. Re-evaluated when the device rotates.
 */
function useIsLandscape(): boolean {
  const query = () => {
    try {
      return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(orientation: landscape)').matches
        : false;
    } catch {
      return false;
    }
  };
  const [landscape, setLandscape] = useState(query);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(orientation: landscape)');
    const onChange = () => setLandscape(mq.matches);
    onChange();
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return landscape;
}

/** Identity badges for one side — the Classic tile header's content, reused in the strip and the tiles. */
function SideIdentity({
  p,
  styleCode,
  sizeText,
  showLocation,
}: {
  p: ScanPlacement;
  styleCode?: string;
  sizeText: string | null;
  showLocation: boolean;
}) {
  const stripped = stripStyleCode(p.designName ?? '', styleCode);
  const nameText =
    stripped && stripped.toUpperCase() !== (styleCode ?? '').toUpperCase() ? stripped : '—';
  return (
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 text-sm">
      {showLocation && (
        <span className="text-base font-extrabold uppercase tracking-wide">{p.location}</span>
      )}
      {styleCode && (
        <span className="rounded-md border px-1.5 py-0.5 font-mono font-bold leading-none">
          {styleCode}
        </span>
      )}
      <span className="min-w-0 truncate font-medium" title={nameText === '—' ? '' : nameText}>
        {nameText}
      </span>
      {p.fromStock && (
        <span className="rounded border-2 border-amber-500 px-2 py-0.5 text-xs font-extrabold uppercase tracking-wide text-amber-500">
          From stock
        </span>
      )}
      <span
        className={cn(
          'rounded px-2 py-0.5 text-xs font-bold',
          p.method === 'screen-print'
            ? 'bg-muted text-foreground'
            : 'bg-foreground text-background',
        )}
      >
        {p.method === 'screen-print' ? 'SP' : 'DTF'}
      </span>
      {sizeText && <span className="font-mono text-xs text-muted-foreground">{sizeText}</span>}
    </div>
  );
}

/**
 * The caption under a side's name. The beam-crossing figure appears only for an ALIGNED design — it is
 * the bridge between the picture and the physical film, and it is noise on a design whose whole file is
 * the reference. Shared so the portrait and landscape layouts cannot drift apart.
 */
function sizeCaption(
  w: string,
  h: string,
  alignViewerBox: InchBox | null,
  crossIn: number | null,
): string {
  const beam = alignViewerBox && crossIn != null ? ` · beam at ${fmtIn(crossIn)} in` : '';
  return `${w} × ${h} in${beam}`;
}

/**
 * One side as a tile — the Classic tile's shape at full size: an optional header (the side's identity,
 * used when several tiles share the screen), the platen frame filling the tile with the lasers that
 * position THIS side bright, the emerald stroke once the side is pressed, and the press bar at the
 * base — the same `PressBar`, the same handler, the only tap target. Warnings appear as one amber
 * badge over the frame, only when there is something to warn about.
 */
function SideTile({
  result,
  p,
  prefs,
  styleCode,
  showHeader,
  pressable,
  pending,
  disarmed,
  onToggle,
}: {
  result: ScanResult;
  p: ScanPlacement;
  prefs: PressPrefs;
  styleCode?: string;
  showHeader: boolean;
  pressable: boolean;
  pending: boolean;
  disarmed: boolean;
  onToggle: () => void;
}) {
  const {
    platen,
    collarEdge,
    box,
    viewerBox,
    alignViewerBox,
    crossIn,
    overflow,
    drawLines,
    note,
    fallbackNote,
    hasDesign,
  } = layoutFor(result, p, prefs);
  const pressed = p.pressed === true;
  const warnings = [
    ...overflow.map((o) => `⚠ Exceeds platen at the ${o.edge} by ${fmtIn(o.byIn)} in`),
    ...(fallbackNote ? [fallbackNote] : []),
    ...(note ? [note] : []),
  ];
  return (
    <div
      data-testid="press-tile"
      className={cn(
        // `flex-1`: as the portrait column's only tile it must GROW to the working height, or the
        // size-contained frame inside it resolves to zero. Inert as a landscape grid item.
        'flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border-2 transition-colors',
        pressed ? 'border-emerald-500 bg-emerald-950/30' : 'border-border bg-card',
      )}
    >
      {showHeader && (
        <div className="flex flex-none items-center gap-2 px-4 pt-3 pb-2">
          <SideIdentity
            p={p}
            styleCode={styleCode}
            sizeText={
              hasDesign ? sizeCaption(fmtIn(box.w), fmtIn(box.h), alignViewerBox, crossIn) : null
            }
            showLocation
          />
        </div>
      )}
      <div className="relative grid min-h-0 flex-1 p-2">
        <PlatenFrame
          platen={platen}
          collarEdge={collarEdge}
          viewerBox={viewerBox}
          alignViewerBox={alignViewerBox}
          overflow={overflow}
          lines={drawLines}
          previewUrl={p.previewUrl}
          alt={p.designName ?? p.location}
        />
        {/* Only when something is wrong: a badge over the frame, never a permanent panel. */}
        {warnings.length > 0 && (
          <div
            role="status"
            className="pointer-events-none absolute top-4 right-4 z-10 max-w-[70%] rounded-md border border-amber-500/50 bg-amber-950/85 px-2.5 py-1.5 text-xs leading-snug text-amber-100"
          >
            {warnings.map((w) => (
              <div key={w}>{w}</div>
            ))}
          </div>
        )}
      </div>
      <PressBar
        method={p.method}
        pressed={pressed}
        pressable={pressable}
        pending={pending}
        disarmed={disarmed}
        onToggle={onToggle}
      />
    </div>
  );
}

/**
 * The Press-mode body. Two layouts, chosen by the device's orientation:
 *
 *  · LANDSCAPE — every side at once, side by side, Classic's grid: the design is height-limited here, so
 *    two frames cost it nothing, and both sides' beams are on screen with their own press bars. No pager;
 *    the auto-advance disarm is not applied either (each bar has its own place).
 *  · PORTRAIT — one side at a time: a slim strip with the side tabs and the identity, then the tile at
 *    full width. Two tiles here would halve the design.
 *
 * Nothing else is on screen by default: the platen's size sits in the frame's corner, and the only text
 * that appears is a warning badge, only when there is something to warn about (owner, 2026-09-10: the
 * numbers are not worth the room; the design is). Rendered by `ScanResultView` in place of the two-tile
 * grid; the status band above and the footer below are the page's, unchanged.
 */
export function PressModeBody({
  result,
  placements,
  styleCodeByDesignId,
  isPressable,
  isPending,
  onToggle,
  controls,
}: {
  result: ScanResult;
  placements: ScanPlacement[];
  styleCodeByDesignId: ReadonlyMap<string, string>;
  isPressable: (p: ScanPlacement) => boolean;
  isPending: (p: ScanPlacement) => boolean;
  onToggle: (p: ScanPlacement) => void;
  controls: PressModeControls;
}) {
  const { prefs, onPrefsChange } = controls;
  const [setupOpen, setSetupOpen] = useState(false);
  const landscape = useIsLandscape();
  const ordered = pagerOrder(placements);
  const idx = Math.min(Math.max(controls.sideIndex, 0), ordered.length - 1);
  const current = ordered[idx];
  if (!current) return null;

  const platen = platenFromPrefs(prefs);
  const laserCount = laserLines(
    lasersFromPrefs(prefs, platen),
    platen,
    prefs.collarEdge,
    new Set(),
  ).length;
  const codeOf = (p: ScanPlacement) =>
    p.designId ? styleCodeByDesignId.get(p.designId) : undefined;
  const openSetup = (open: boolean) => {
    setSetupOpen(open);
    controls.onPickerOpenChange(open);
  };
  const sizeOf = (p: ScanPlacement) => {
    if (p.widthIn == null || p.heightIn == null || p.method === 'screen-print') return null;
    const { alignViewerBox, crossIn } = layoutFor(result, p, prefs);
    return sizeCaption(fmtIn(p.widthIn), fmtIn(p.heightIn), alignViewerBox, crossIn);
  };
  const toolButtons = (
    <div className="flex flex-none items-center gap-1">
      <button
        type="button"
        aria-pressed={prefs.guides}
        aria-label={prefs.guides ? 'Guides on' : 'Guides off'}
        title={
          prefs.guides ? 'Guides on — tap to hide the beams' : 'Guides off — tap to show the beams'
        }
        onClick={() => onPrefsChange({ ...prefs, guides: !prefs.guides })}
        className={cn(
          'flex size-11 items-center justify-center rounded-lg border',
          prefs.guides ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        {prefs.guides ? <Eye className="size-5" /> : <EyeOff className="size-5" />}
      </button>
      <button
        type="button"
        aria-label={`Setup · ${laserCount} laser${laserCount === 1 ? '' : 's'}`}
        title={`Press setup — ${laserCount} laser${laserCount === 1 ? '' : 's'} on`}
        onClick={() => openSetup(true)}
        className="flex size-11 items-center justify-center rounded-lg border text-muted-foreground hover:text-foreground"
      >
        <Crosshair className="size-5 text-[#ff6b63]" />
      </button>
    </div>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 p-4 sm:p-5">
      {landscape ? (
        <>
          <div className="flex flex-none items-center justify-end">{toolButtons}</div>
          <div
            data-testid="press-tiles"
            className="grid min-h-0 flex-1 gap-4"
            style={{ gridTemplateColumns: `repeat(${ordered.length}, minmax(0, 1fr))` }}
          >
            {ordered.map((p) => (
              <SideTile
                key={`${p.location}-${p.designId}`}
                result={result}
                p={p}
                prefs={prefs}
                styleCode={codeOf(p)}
                showHeader
                pressable={isPressable(p)}
                pending={isPending(p)}
                disarmed={false}
                onToggle={() => onToggle(p)}
              />
            ))}
          </div>
        </>
      ) : (
        <>
          {/* The strip: which side, what it is, and the two rarely-touched buttons. One row. */}
          <div className="flex flex-none flex-wrap items-center gap-2">
            <div className="flex overflow-hidden rounded-lg border">
              {ordered.map((p, i) => (
                <button
                  key={`${p.location}-${p.designId}`}
                  type="button"
                  aria-pressed={i === idx}
                  onClick={() => controls.onSideChange(i)}
                  className={cn(
                    'flex min-h-[44px] items-center justify-center gap-1.5 px-4 text-xs font-extrabold uppercase tracking-wide',
                    i === idx ? 'bg-foreground text-background' : 'text-muted-foreground',
                  )}
                >
                  <span>{p.location}</span>
                  {p.pressed && (
                    <Check
                      className={cn(
                        'size-4 flex-none',
                        i === idx ? 'text-emerald-700' : 'text-emerald-400',
                      )}
                      aria-label="pressed"
                    />
                  )}
                </button>
              ))}
            </div>
            <SideIdentity
              p={current}
              styleCode={codeOf(current)}
              sizeText={sizeOf(current)}
              showLocation={false}
            />
            {toolButtons}
          </div>
          <SideTile
            result={result}
            p={current}
            prefs={prefs}
            styleCode={codeOf(current)}
            showHeader={false}
            pressable={isPressable(current)}
            pending={isPending(current)}
            disarmed={controls.barDisarmed}
            onToggle={() => onToggle(current)}
          />
        </>
      )}

      <PressSetupSheet
        open={setupOpen}
        onOpenChange={openSetup}
        prefs={prefs}
        onPrefsChange={onPrefsChange}
      />
    </div>
  );
}
